import React, { useState } from 'react';
import './ProfessionalUI.css';

interface ProfessionalCardProps {
  children: React.ReactNode;
  title?: string;
  icon?: React.ReactNode;
  className?: string;
  headerAction?: React.ReactNode;
  variant?: 'default' | 'glass' | 'elevated' | 'gradient';
  padding?: 'sm' | 'md' | 'lg';
  hover?: boolean;
}

export const ProfessionalCard: React.FC<ProfessionalCardProps> = ({
  children,
  title,
  icon,
  className = '',
  headerAction,
  variant = 'default',
  padding = 'md',
  hover = false
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardClasses = [
    'professional-card',
    `professional-card--${variant}`,
    `professional-card--padding-${padding}`,
    hover && 'professional-card--hover',
    isHovered && 'professional-card--hovered',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cardClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {(title || icon || headerAction) && (
        <div className="professional-card__header">
          <div className="professional-card__title-group">
            {icon && <div className="professional-card__icon">{icon}</div>}
            {title && <h3 className="professional-card__title">{title}</h3>}
          </div>
          {headerAction && (
            <div className="professional-card__action">{headerAction}</div>
          )}
        </div>
      )}
      <div className="professional-card__content">
        {children}
      </div>
    </div>
  );
};

interface ProfessionalButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'gradient' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  onClick?: () => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
}

export const ProfessionalButton: React.FC<ProfessionalButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  onClick,
  className = '',
  type = 'button'
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const buttonClasses = [
    'professional-button',
    `professional-button--${variant}`,
    `professional-button--size-${size}`,
    loading && 'professional-button--loading',
    disabled && 'professional-button--disabled',
    isHovered && !disabled && !loading && 'professional-button--hovered',
    className
  ].filter(Boolean).join(' ');

  const renderIcon = () => {
    if (!icon) return null;

    return (
      <span className={`professional-button__icon professional-button__icon--${iconPosition}`}>
        {icon}
      </span>
    );
  };

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {loading && (
        <span className="professional-button__spinner">
          <svg className="animate-spin" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </span>
      )}

      {iconPosition === 'left' && renderIcon()}
      <span className="professional-button__text">{children}</span>
      {iconPosition === 'right' && renderIcon()}
    </button>
  );
};

interface StatusIndicatorProps {
  status: 'success' | 'warning' | 'error' | 'info' | 'loading';
  text?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'dot' | 'badge' | 'full';
  className?: string;
}

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  text,
  size = 'md',
  variant = 'dot',
  className = ''
}) => {
  const indicatorClasses = [
    'status-indicator',
    `status-indicator--${status}`,
    `status-indicator--size-${size}`,
    `status-indicator--${variant}`,
    className
  ].filter(Boolean).join(' ');

  const renderDot = () => (
    <span className="status-indicator__dot">
      <span className="status-indicator__pulse"></span>
    </span>
  );

  const renderContent = () => {
    if (variant === 'dot') return renderDot();

    return (
      <div className="status-indicator__content">
        {variant === 'badge' && renderDot()}
        {status === 'loading' ? (
          <div className="status-indicator__spinner">
            <svg className="animate-spin" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : renderDot()}
        {text && (
          <span className="status-indicator__text">{text}</span>
        )}
      </div>
    );
  };

  return <div className={indicatorClasses}>{renderContent()}</div>;
};

interface ProgressBarProps {
  value: number;
  max?: number;
  label?: string;
  showPercentage?: boolean;
  variant?: 'default' | 'gradient' | 'segments';
  size?: 'sm' | 'md' | 'lg';
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
  className?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  label,
  showPercentage = false,
  variant = 'default',
  size = 'md',
  color = 'blue',
  className = ''
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  const progressBarClasses = [
    'progress-bar',
    `progress-bar--${variant}`,
    `progress-bar--size-${size}`,
    `progress-bar--color-${color}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={progressBarClasses}>
      {(label || showPercentage) && (
        <div className="progress-bar__header">
          {label && <span className="progress-bar__label">{label}</span>}
          {showPercentage && (
            <span className="progress-bar__percentage">{percentage.toFixed(1)}%</span>
          )}
        </div>
      )}
      <div className="progress-bar__track">
        <div
          className="progress-bar__fill"
          style={{ width: `${percentage}%` }}
        >
          {variant === 'gradient' && (
            <div className="progress-bar__gradient"></div>
          )}
        </div>
      </div>
    </div>
  );
};

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease' | 'neutral';
  };
  icon?: React.ReactNode;
  description?: string;
  variant?: 'default' | 'gradient' | 'glass';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  icon,
  description,
  variant = 'default',
  size = 'md',
  className = ''
}) => {
  const metricClasses = [
    'metric-card',
    `metric-card--${variant}`,
    `metric-card--size-${size}`,
    className
  ].filter(Boolean).join(' ');

  const renderChange = () => {
    if (!change) return null;

    const changeClasses = [
      'metric-card__change',
      `metric-card__change--${change.type}`
    ].join(' ');

    return (
      <div className={changeClasses}>
        <span className="metric-card__change-icon">
          {change.type === 'increase' ? '↑' : change.type === 'decrease' ? '↓' : '→'}
        </span>
        <span className="metric-card__change-value">{Math.abs(change.value)}%</span>
      </div>
    );
  };

  return (
    <div className={metricClasses}>
      <div className="metric-card__header">
        <div className="metric-card__title-group">
          {icon && <div className="metric-card__icon">{icon}</div>}
          <span className="metric-card__title">{title}</span>
        </div>
        {renderChange()}
      </div>
      <div className="metric-card__value">{value}</div>
      {description && (
        <div className="metric-card__description">{description}</div>
      )}
    </div>
  );
};

interface TabsProps {
  tabs: Array<{
    id: string;
    label: string;
    icon?: React.ReactNode;
    content: React.ReactNode;
    badge?: string | number;
  }>;
  defaultTab?: string;
  onTabChange?: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'underline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTab,
  onTabChange,
  variant = 'default',
  size = 'md',
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  const tabsClasses = [
    'tabs',
    `tabs--${variant}`,
    `tabs--size-${size}`,
    className
  ].filter(Boolean).join(' ');

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className={tabsClasses}>
      <div className="tabs__nav">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tabs__tab ${activeTab === tab.id ? 'tabs__tab--active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.icon && <span className="tabs__tab-icon">{tab.icon}</span>}
            <span className="tabs__tab-label">{tab.label}</span>
            {tab.badge && (
              <span className="tabs__tab-badge">{tab.badge}</span>
            )}
          </button>
        ))}
      </div>
      <div className="tabs__content">
        {activeTabContent}
      </div>
    </div>
  );
};

const ProfessionalUI = {
  ProfessionalCard,
  ProfessionalButton,
  StatusIndicator,
  ProgressBar,
  MetricCard,
  Tabs
};

export default ProfessionalUI;